import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Gift, CreditCard, Smartphone, MapPin, MessageCircleHeart, Bike, ShieldCheck, Share2, ArrowRight, UtensilsCrossed, PhoneCall } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "s1", icon: <UtensilsCrossed className="w-6 h-6" /> },
  { key: "s2", icon: <MessageCircleHeart className="w-6 h-6" /> },
  { key: "s3", icon: <CreditCard className="w-6 h-6" /> },
  { key: "s4", icon: <Bike className="w-6 h-6" /> },
];

export default function GiftMeal() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const startGift = () => {
    try { localStorage.setItem("neldaari_gift_intent", "1"); } catch (e) { void e; }
    navigate("/restaurants");
  };

  const share = async () => {
    const url = `${window.location.origin}/offrir`;
    const text = t("gm.shareText");
    if (navigator.share) {
      try { await navigator.share({ title: t("gm.shareTitle"), text, url }); return; } catch (e) { void e; }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("gm.linkCopied"));
    } catch (e) {
      window.open(`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`, "_blank");
    }
  };

  return (
    <div data-testid="gift-meal-page">
      {/* HERO */}
      <section className="bg-ink text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-yellow/20 text-brand-yellow px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] mb-5">
              <Gift className="w-4 h-4" /> {t("gm.badge")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4">
              {t("gm.heroTitle")} <span className="text-brand-yellow">{t("gm.heroCity")}</span>
            </h1>
            <p className="text-white/80 text-base leading-relaxed mb-8 max-w-lg">
              {t("gm.heroText")}
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={startGift}
                data-testid="gift-cta-start"
                className="inline-flex items-center gap-2 rounded-full bg-brand-red hover:bg-[#C81F2D] text-white px-7 py-3.5 font-semibold text-base shadow-lg shadow-brand-red/30 active:scale-[0.98] transition-transform"
              >
                <Gift className="w-5 h-5" /> {t("gm.ctaStart")} <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={share}
                data-testid="gift-cta-share"
                className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 hover:border-white text-white px-6 py-3.5 font-semibold text-sm"
              >
                <Share2 className="w-4 h-4" /> {t("gm.ctaShare")}
              </button>
            </div>
          </div>
          <div className="hidden md:block">
            <div className="rounded-3xl bg-white/5 border border-white/10 p-6 space-y-4">
              <div className="rounded-2xl bg-white text-ink p-4 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-yellow/30 grid place-items-center shrink-0">🎁</div>
                <div>
                  <div className="text-sm font-bold">{t("gm.cardGift")}</div>
                  <div className="text-xs text-mute mt-0.5">{t("gm.cardGiftMsg")}</div>
                </div>
              </div>
              <div className="rounded-2xl bg-white text-ink p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#E7F6EE] grid place-items-center shrink-0"><Bike className="w-5 h-5 text-brand-green" /></div>
                  <div>
                    <div className="text-sm font-bold">{t("gm.cardDriver")}</div>
                    <div className="text-xs text-mute">{t("gm.cardDishes")}</div>
                  </div>
                </div>
                <span className="text-xs font-bold text-brand-green">12 min</span>
              </div>
              <div className="rounded-2xl bg-white text-ink p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FFF5F6] grid place-items-center shrink-0"><PhoneCall className="w-5 h-5 text-brand-red" /></div>
                <div>
                  <div className="text-sm font-bold">{t("gm.cardThanks")}</div>
                  <div className="text-xs text-mute">{t("gm.cardThanksHint")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-xs uppercase tracking-[0.25em] text-brand-red mb-2">{t("gm.howLabel")}</div>
        <h2 className="font-display text-lg font-semibold mb-8">{t("gm.howTitle")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s) => (
            <div key={s.key} className="bg-white border border-sand rounded-2xl p-5">
              <div className="w-12 h-12 rounded-2xl bg-brand-red/10 text-brand-red grid place-items-center mb-4">{s.icon}</div>
              <div className="font-display font-semibold mb-1.5">{t(`gm.steps.${s.key}.title`)}</div>
              <p className="text-sm text-mute leading-relaxed">{t(`gm.steps.${s.key}.text`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PAIEMENT + CONFIANCE */}
      <section className="bg-elevated">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid md:grid-cols-3 gap-6">
          <div className="bg-white border border-sand rounded-2xl p-5 flex items-start gap-3">
            <CreditCard className="w-6 h-6 text-brand-red shrink-0" />
            <div>
              <div className="font-semibold text-sm mb-1">{t("gm.payAnywhere")}</div>
              <p className="text-sm text-mute">{t("gm.payAnywhereText")}</p>
            </div>
          </div>
          <div className="bg-white border border-sand rounded-2xl p-5 flex items-start gap-3">
            <MapPin className="w-6 h-6 text-brand-green shrink-0" />
            <div>
              <div className="font-semibold text-sm mb-1">{t("gm.liveTracking")}</div>
              <p className="text-sm text-mute">{t("gm.liveTrackingText")}</p>
            </div>
          </div>
          <div className="bg-white border border-sand rounded-2xl p-5 flex items-start gap-3">
            <ShieldCheck className="w-6 h-6 text-ink shrink-0" />
            <div>
              <div className="font-semibold text-sm mb-1">{t("gm.handDelivery")}</div>
              <p className="text-sm text-mute">{t("gm.handDeliveryText")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <div className="rounded-3xl bg-brand-red text-white px-6 py-12">
          <Gift className="w-10 h-10 mx-auto mb-4" />
          <h2 className="font-display text-lg font-semibold mb-2">{t("gm.finalTitle")}</h2>
          <p className="text-white/85 text-sm mb-6 max-w-md mx-auto">
            {t("gm.finalText")}
          </p>
          <button
            onClick={startGift}
            data-testid="gift-cta-bottom"
            className="inline-flex items-center gap-2 rounded-full bg-white text-brand-red px-8 py-3.5 font-bold text-base active:scale-[0.98] transition-transform"
          >
            <Smartphone className="w-5 h-5" /> {t("gm.chooseRestaurant")}
          </button>
          <div className="text-xs text-white/70 mt-4">
            {t("gm.already")} <Link to="/login" className="underline font-semibold">{t("gm.loginLink")}</Link> {t("gm.loginSuffix")}
          </div>
        </div>
      </section>
    </div>
  );
}
